import PanelError from '@wandb/weave/common/components/elements/PanelError';
import React, {memo, useMemo, useState} from 'react';

import {DEFAULT_LINE_PLOT_MAX_GROUP_RUNS} from '../../components/WorkspaceDrawer/Settings/runLinePlots/maxRunDefaults';
import {IconPositioningContextProvider} from '../../state/issues/IconPositioningContext';
import {prettyXAxisLabel} from '../../util/plotHelpers/axis';
import {
  getPlotFontSize,
  plotFontSizeToPx,
} from '../../util/plotHelpers/plotFontSize';
import {useResizeObserverDebounced} from '../../util/useResizeObserverDebounced';
import {PanelPopupMode} from '../Panel/types';
import {PanelTitleView} from '../PanelTitle/PanelTitleView';
import {LinePlot} from '../vis/LinePlot';
import {BarChart} from './BarChart';
import {CappedPanelDelayRender} from './CappedPanelDelayRender';
import {getTitleFromConfig} from './common';
import {getErrorMessage} from './getGraphError';
import {partitionLines} from './graphLines';
import {type GraphRenderProps} from './graphRenderProps';
import {IssueMarkerList} from './IssueMarkerList';
import {getIssuesWrapperStatus} from './issueMarkers';
import {PlotLayoutView} from './Layout';
import {LinePlotLegendView} from './LinePlotLegendView';
import {LinePlotLegendWarningView} from './LinePlotLegendWarningView';
import {LinePlotSettingsProvider} from './LinePlotSettingsContext';
import {MissingRunBanner} from './MissingRunBanner';
import {useLinePlotZoomUsageTracker} from './observability/zoomUsage';
import {SamplingWarningView} from './SamplingWarningView';
import {getIsLegendShown} from './useLegendVisibility';
import {useLinesForDisplay} from './useLinesForDisplay';
import {
  linesHaveRenderableData,
  useRegexMetricSettlingLoading,
} from './useRegexMetricSettlingLoading';
import {isBarPlot} from './utils/checkTypes';
import {getGraphZoom} from './zoom/getGraphZoom';

const expandStyles: React.CSSProperties = {
  height: '100%',
  width: '100%',
};

const SAMPLING_WARNING_STYLE: React.CSSProperties = {zIndex: 1};

/**
 * Highest per-runset run count — the signal the multi-runset warning path
 * keys off of. `Math.max(...[])` is `-Infinity`, so guard on empty map.
 */
function getMaxFetchedRunCount(
  countsByRunsetId: Record<string, number> | undefined
): number {
  const values = Object.values(countsByRunsetId ?? {});
  if (values.length === 0) {
    return 0;
  }
  return Math.max(...values);
}

const GraphComp: React.FC<GraphRenderProps> = props => {
  // destructure
  const {
    lines,
    config,
    issueLines = [],
    loading,
    runNameTruncationType,
  } = props;

  // state
  const [isHovered, setIsHovered] = useState(false);

  const {
    isResizing,
    height = 1,
    ref: elementRef,
    width = 1,
  } = useResizeObserverDebounced<HTMLDivElement>(1);
  const {excludeOutliers, singlePointRenderMode, showLegend} = props.display;
  const {
    isFullFidelity,
    isMultiMetric,
    isSingleRun,
    xAxis,
    xLogScale,
    yLogScale,
  } = props.plotSettings;
  // Issue lines past a zoomed x bound are hidden, and a zoomed x axis skips
  // the domain mismatch check.
  const {queryZoom} = props.zoom.fullFidelity;
  const isZoomedXMin = queryZoom.xAxisMin != null;
  const isZoomedXMax = queryZoom.xAxisMax != null;

  const fontSize = getPlotFontSize(config.fontSize ?? 'auto', height);

  // Additional constraints are applied for legends depending on panel width/height and if
  // view is single run
  const isLegendShown = getIsLegendShown({
    isMultiMetric,
    isSingleRun,
    panelSize: {height, width},
    showLegend,
  });

  const timestep = lines?.[0]?.timestep;

  const xScale = xLogScale ? 'log' : 'linear';
  const yScale = yLogScale ? 'log' : 'linear';

  const {filteredLines} = useLinesForDisplay(lines, {
    xScale,
    yScale,
    isFullFidelity,
    isHovered,
  });
  const isRegexMetricSettling = useRegexMetricSettlingLoading(
    config,
    filteredLines
  );
  const effectiveLoading = loading || isRegexMetricSettling;

  const {legendLines, visibleLines, hiddenLines} = useMemo(
    function partitionDisplayLines() {
      return partitionLines(filteredLines);
    },
    [filteredLines]
  );
  const trackZoomUsage = useLinePlotZoomUsageTracker({
    ...props.zoomUsage,
    lines: visibleLines,
    metrics: config.metrics,
    panelId: props.panelId,
    xAxis,
  });
  /**
   * Notes on timestep:
   * 1. Timestep is determined by examining the lines data and determining a proper scale
   * 2. The timestep is saved across the panel through the interaction context
   * 3. when we zoom we don't want to change the timestep factor
   */
  const {activeZoom, xDomain, yDomain, zoomCallback, zooming} = getGraphZoom(
    props.zoom,
    props.plotSettings,
    timestep,
    trackZoomUsage
  );

  // derived values
  const [errorDetail, errorMessage] = getErrorMessage(
    config,
    props.cappedPanel.isDelayed,
    filteredLines,
    {
      activeZoom,
      zoomCallback,
    }
  );

  const renderBarChart = isBarPlot(props.lines, zooming, singlePointRenderMode);
  const renderPanelError = !effectiveLoading && errorMessage != null;

  const [isDrawing, setIsDrawing] = useState(false);

  const panelErrorMetaInfo = {isFullFidelity};

  // A full-screen panel shows its title without search highlighting.
  const highlightTitleSearch = props.panelTitle.isFullScreen
    ? undefined
    : props.panelTitle.makeHighlightSearchTitle;
  function openPanelEditor() {
    props.panelTitle.setPanelPopupMode(PanelPopupMode.EDIT);
  }
  // Hovering a legend run name highlights that run across panels.
  function highlightHoveredRun(
    runId: string | undefined,
    metricName: string | undefined
  ) {
    props.highlight.setHighlight('run:name', runId, metricName);
  }

  const warning = (
    <LinePlotLegendWarningView
      groupingDepth={props.plotSettings.groupingDepth}
      isFullFidelity={props.plotSettings.isFullFidelity}
      isGrouped={props.plotSettings.isAnyRunsetGrouped}
      maxRuns={props.plotSettings.limit}
      hasSubsampledGroups={props.data.hasSubsampledGroups ?? false}
      isMultiMetric={isMultiMetric ?? false}
      loading={effectiveLoading}
      metrics={config.metrics?.length ?? 0}
      isReadOnly={props.readOnly ?? false}
      lines={legendLines}
      maxFetchedRunCount={getMaxFetchedRunCount(
        props.data.fetchedRunCountByRunsetId
      )}
      maxGroupRuns={config.groupRunsLimit ?? DEFAULT_LINE_PLOT_MAX_GROUP_RUNS}
      runCount={props.data.fetchedRunCount ?? 0}
      runsetCount={
        Object.keys(props.data.fetchedRunCountByRunsetId ?? {}).length
      }
      totalGroupedIncludedRuns={props.data.totalGroupedIncludedRuns ?? 0}
      disallowSelect={isDrawing}
    />
  );

  if (
    renderBarChart ||
    renderPanelError ||
    (props.hasError && !effectiveLoading)
  ) {
    return (
      <div
        className="chart"
        data-test={`runs-line-plot-error-${
          props.config.xAxis ?? 'unknown-x-axis'
        }`}
        onMouseEnter={function markErrorPanelHovered() {
          setIsHovered(true);
        }}
        onMouseLeave={function clearErrorPanelHovered() {
          setIsHovered(false);
        }}
        ref={elementRef}>
        <PanelTitleView
          title={getTitleFromConfig(config)}
          fontSize={fontSize}
          highlightSearch={highlightTitleSearch}
          onTitleClick={openPanelEditor}
        />
        {renderBarChart && warning}
        <div className="chart-content" data-test="chart-content">
          {renderPanelError ? (
            <PanelError
              panelViewType="Run History Line Plot"
              panelID={props.panelId}
              metaInfo={panelErrorMetaInfo}
              message={
                props.hasError ? (
                  <div>
                    An unexpected error was detected. Will try again shortly.
                  </div>
                ) : (
                  errorMessage
                )
              }
              logErrorMessage={
                props.hasError
                  ? 'An unexpected error was detected. Will try again shortly.'
                  : typeof errorDetail === 'string'
                    ? errorDetail
                    : 'Chart error occurred'
              }
            />
          ) : (
            <BarChart lines={lines} config={config} />
          )}
        </div>
      </div>
    );
  }

  const issueStatus = getIssuesWrapperStatus({
    annotationActive: props.issueMarkers.annotationActive,
    issueCount: props.issueMarkers.activeIssueCount,
    issueRampStatus: props.issueMarkers.issueRampStatus,
  });

  return (
    <div
      data-x-axis={config.xAxis ?? 'unknown-x-axis'}
      data-test="chart-content"
      ref={elementRef}
      style={expandStyles}>
      <IconPositioningContextProvider issues={issueLines} width={width}>
        <PlotLayoutView
          delayed={props.cappedPanel.isDelayed}
          DelayWarning={
            <CappedPanelDelayRender
              {...props.cappedPanel}
              panelId={props.panelId}
              title={getTitleFromConfig(config)}
            />
          }
          IssueMarkers={<IssueMarkerList {...props.issueMarkers} />}
          issueStatus={issueStatus}
          fontSize={plotFontSizeToPx.legend[fontSize]}
          handleHover={setIsHovered}
          loading={effectiveLoading}
          numLines={
            linesHaveRenderableData(visibleLines) ? visibleLines.length : 0
          }
          Graph={
            isResizing ? (
              <div />
            ) : (
              <LinePlot
                excludeOutliers={excludeOutliers}
                filteredLines={visibleLines}
                fontSize={fontSize}
                highlightSource={props.highlight.source}
                isDrawing={isDrawing}
                isHovered={isHovered}
                issueLines={issueLines}
                hoveredIssueSource={props.issueMarkers.hoveredIssueSource}
                isZoomedXMax={isZoomedXMax}
                isZoomedXMin={isZoomedXMin}
                panelId={props.panelId}
                runNameTruncationType={runNameTruncationType}
                setHighlights={props.highlight.setHighlights}
                setIsDrawing={setIsDrawing}
                svg={props.exportMode}
                timestep={timestep}
                xAxis={prettyXAxisLabel(xAxis, props.lines)}
                xAxisTitle={config.xAxisTitle}
                xDomain={xDomain}
                xScale={xScale}
                yAxis={''}
                yAxisTitle={config.yAxisTitle}
                yDomain={yDomain}
                yScale={yLogScale ? 'log' : 'linear'}
                zoomCallback={zoomCallback}
                zooming={zooming}>
                {
                  <MissingRunBanner
                    highlightSource={props.highlight.source}
                    lines={visibleLines}
                  />
                }
              </LinePlot>
            )
          }
          legendPosition={config.legendPosition ?? 'north'}
          Legend={
            <LinePlotLegendView
              colorPalette={props.plotSettings.colorPaletteOption}
              colorRunNames={props.plotSettings.colorRunNames}
              disableRunLinks={props.disableRunLinks}
              fontSize={fontSize}
              hiddenLines={hiddenLines}
              isMultiMetric={props.plotSettings.isMultiMetric}
              isSingleRun={props.plotSettings.isSingleRun}
              lines={legendLines}
              isVertical={
                !['east', 'west'].includes(config.legendPosition ?? '')
              }
              disallowSelect={isDrawing}
              onRunHover={highlightHoveredRun}
            />
          }
          showLegend={isLegendShown}
          sizeRef={props.sizeRef}
          Title={
            <PanelTitleView
              title={getTitleFromConfig(config)}
              fontSize={fontSize}
              disallowSelect={isDrawing}
              highlightSearch={highlightTitleSearch}
              onTitleClick={openPanelEditor}
            />
          }
          Warning={warning}
        />
      </IconPositioningContextProvider>
      <SamplingWarningView
        {...props.samplingWarning}
        style={SAMPLING_WARNING_STYLE}
        width={width}
      />
    </div>
  );
};
const Graph = memo(GraphComp);
Graph.displayName = 'Graph';

// TODO(axel): This wrapper is only here because I'm not confident that some props are guaranteed to exist.
// Find out what's up and kill this wrapper.
const GraphWrapperComp: React.FC<GraphRenderProps> = function GraphWrapperComp(
  props
) {
  if (props.data.histories == null) {
    return <p>No Histories</p>;
  }

  return (
    <LinePlotSettingsProvider settings={props.plotSettings}>
      <Graph {...props} />
    </LinePlotSettingsProvider>
  );
};
export const GraphWrapper = memo(GraphWrapperComp);
GraphWrapper.displayName = 'GraphWrapper';
